import type {
  HTMLAttributes,
  PropsWithChildren,
  TableHTMLAttributes,
} from 'react';

import type { ColorKeys } from '@/theme/types/colors';

import type { TextColorProps } from '../Box';
import { Typography } from '../Typography';

import * as S from './Table.styles';

export type TableAlign = 'left' | 'center' | 'right';

export const Table = ({
  children,
  ...props
}: PropsWithChildren<TableHTMLAttributes<HTMLTableElement>>) => {
  return <S.Table {...props}>{children}</S.Table>;
};

export interface TableTitleProps extends TextColorProps {
  height?: string;
  title: string;
}
export const TableTitle = ({
  children: bodyText,
  color = 'white',
  height,
  title,
}: PropsWithChildren<TableTitleProps>) => {
  return (
    <caption>
      <S.TableTitleWrapper
        $hasBodyText={!!bodyText}
        $height={height}
        alignItems='center'
        color={color}
        gridGap={1}
      >
        <Typography fontWeight='semibold' scale='h5' textTransform='uppercase'>
          {title}
        </Typography>

        {bodyText && (
          <S.TableTitleBodyTextWrapper color='white'>
            {bodyText}
          </S.TableTitleBodyTextWrapper>
        )}
      </S.TableTitleWrapper>
    </caption>
  );
};

export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  color?: ColorKeys;
}

export const TableHead = ({
  children,
  color = 'white',
  ...props
}: PropsWithChildren<TableHeadProps>) => {
  return (
    <S.TableHead $color={color} {...props}>
      {children}
    </S.TableHead>
  );
};

export const TableBody = ({
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLTableSectionElement>>) => {
  return <tbody {...props}>{children}</tbody>;
};

export const TableRow = ({
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLTableRowElement>>) => {
  return <tr {...props}>{children}</tr>;
};

export interface TableCellProps extends HTMLAttributes<HTMLTableCellElement> {
  align?: TableAlign;
  colSpan?: number;
  component?: 'td' | 'th';
  lightGrey?: boolean;
  rowSpan?: number;
  scope?: string;
  width?: string | number;
}
export const TableCell = ({
  align = 'left',
  children,
  colSpan,
  component = 'td',
  lightGrey,
  rowSpan,
  scope,
  width,
  ...props
}: PropsWithChildren<TableCellProps>) => {
  return (
    <S.TableCell
      $align={align}
      $lightGrey={lightGrey}
      as={component}
      colSpan={colSpan}
      rowSpan={rowSpan}
      scope={scope}
      width={width}
      {...props}
    >
      {children}
    </S.TableCell>
  );
};
